import { Sparkles, Wand2, Zap, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useInViewAnimation } from '@/hooks/useInViewAnimation';

interface FeatureContent {
  id: string;
  section_key: string;
  title: string | null;
  description: string | null;
  video_url: string | null;
  image_url: string | null;
}

interface FeatureBlocksSectionProps {
  onStartCreating?: () => void;
}

// Hardcoded GCS video URLs - direct display without DB fetching
const FEATURE_VIDEOS = {
  feature_ai_story: 'https://storage.googleapis.com/prompt-director-base44/video-examples/hero-1765701335120-b86e6c652_Cinderella_CTA_subs4.mp4',
  feature_veo3: 'https://storage.googleapis.com/prompt-director-base44/video-examples/hero-1765701304567-2708f0f8e_Sequence_ContentCreation1.mp4',
};

const features = [
  {
    id: '1',
    section_key: 'feature_ai_story',
    icon: Sparkles,
    title: 'AI-powered story creation',
    description: 'Transform any vision: Generate video storytelling in seconds. Turn your ideas into engaging video narratives. Simply describe your vision, whether it\'s a campaign, training content, product showcase, or personal story. Our AI generates structured, Veo 3-optimized prompts that create professional videos without trial and error.',
    video_url: FEATURE_VIDEOS.feature_ai_story,
    image_url: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=400&fit=crop',
    reverse: false,
    ctaText: 'Generate Prompts Now',
    listItems: ['Smart narrative structure', 'Emotional story arcs', 'Export-ready prompts'],
  },
  {
    id: '2',
    section_key: 'feature_veo3',
    icon: Wand2,
    title: 'Precision prompts: Veo 3 optimized scenes that work',
    description: 'Elevate every project with prompts that deliver. Get precise, ready to use scene descriptions expertly crafted for Google\'s Veo 3, ensuring your content achieves cinematic quality without extensive storyboarding or creative guesswork.',
    video_url: FEATURE_VIDEOS.feature_veo3,
    image_url: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=600&h=400&fit=crop',
    reverse: true,
    ctaText: 'Generate Prompts Now',
    listItems: ['Consistent visual style', 'Character continuity', 'Brand-aligned messaging'],
  },
];

const iconMap: Record<string, typeof Sparkles> = {
  'feature_ai_story': Sparkles,
  'feature_veo3': Wand2,
};

function FeatureBlock({ 
  feature, 
  index, 
  onStartCreating 
}: { 
  feature: typeof features[0]; 
  index: number; 
  onStartCreating?: () => void;
}) {
  const { ref, isInView } = useInViewAnimation({ threshold: 0.2 });
  const Icon = iconMap[feature.section_key] || Sparkles;
  const reverse = index % 2 === 1;
  
  return (
    <div
      ref={ref}
      className={`flex flex-col ${reverse ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-12 lg:gap-16 mb-24 lg:mb-32 last:mb-0`}
    >
      {/* Video/Image */}
      <div 
        className={`flex-1 opacity-0 ${isInView ? (reverse ? 'animate-slide-in-right' : 'animate-slide-in-left') : ''}`}
      >
        <div className="relative rounded-2xl overflow-hidden shadow-xl border border-border group">
          {/* Subtle glow on hover */}
          <div className="absolute -inset-0.5 bg-gradient-to-r from-primary/20 to-primary/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm" />
          
          <div className="relative">
            {feature.video_url ? (
              <video
                src={feature.video_url}
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-auto pointer-events-none transition-transform duration-700 group-hover:scale-[1.02]"
              />
            ) : (
              <img
                src={feature.image_url || ''}
                alt={feature.title || ''}
                className="w-full h-auto transition-transform duration-700 group-hover:scale-[1.02]"
              />
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div 
        className={`flex-1 space-y-6 opacity-0 ${isInView ? (reverse ? 'animate-slide-in-left' : 'animate-slide-in-right') : ''}`}
        style={{ animationDelay: '100ms' }}
      >
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 transition-all duration-300 hover:bg-primary/20 hover:scale-105">
          <Icon className="w-7 h-7 text-primary" />
        </div>
        <h3 className="text-3xl lg:text-4xl font-bold text-foreground leading-tight">
          {feature.title}
        </h3>
        <p className="text-lg text-muted-foreground leading-relaxed">
          {feature.description}
        </p>
        <ul className="space-y-3 pt-2">
          {feature.listItems.map((item, i) => (
            <li 
              key={i} 
              className="flex items-center gap-3 text-foreground"
            >
              <div className="w-6 h-6 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
                <Zap className="w-3.5 h-3.5 text-primary" />
              </div>
              <span className="font-medium">{item}</span>
            </li>
          ))}
        </ul>
        {onStartCreating && (
          <Button 
            onClick={onStartCreating}
            className="mt-6 font-semibold group/btn"
            size="lg"
          >
            {feature.ctaText}
            <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover/btn:translate-x-1" />
          </Button>
        )}
      </div>
    </div>
  );
}

export function FeatureBlocksSection({ onStartCreating }: FeatureBlocksSectionProps) {
  return (
    <section className="py-24 lg:py-32 bg-muted/30">
      <div className="max-w-6xl mx-auto px-6">
        {features.map((feature, index) => (
          <FeatureBlock 
            key={feature.id}
            feature={feature}
            index={index}
            onStartCreating={onStartCreating}
          />
        ))}
      </div>
    </section>
  );
}
